import { useCallback, useEffect, useState } from "react";
import { FiEye, FiPrinter, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { getPosBillReceipt, getPosBills } from "../../services/pos";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const indiaDate = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(
    new Date(),
  );

const gstLabel = (rate) => `${Number(rate || 0).toFixed(2)}%`;

function GstSummary({ rows = [] }) {
  if (!rows.length) return <span className="text-gray-400">—</span>;

  return (
    <div className="space-y-1 text-right text-xs">
      {rows.map((row) => (
        <p key={row.taxRate} className="whitespace-nowrap text-gray-700">
          <span className="font-semibold text-primary-800">
            {gstLabel(row.taxRate)}
          </span>{" "}
          <span className="text-gray-500">{money(row.taxAmount)}</span>
        </p>
      ))}
    </div>
  );
}

function printBill(bill) {
  const rows = bill.items
    .map(
      (item, index) => `
        <tr>
          <td>${index + 1}</td>
          <td><b>${item.productNameSnapshot}</b><br/><small>${item.variantNameSnapshot || "Default"} · ${item.skuSnapshot}</small></td>
          <td style="text-align:right">${item.quantity}</td>
          <td style="text-align:right">${money(item.unitPrice)}</td>
          <td style="text-align:right">${gstLabel(item.taxRate)}</td>
          <td style="text-align:right">${money(item.lineTaxAmount)}</td>
          <td style="text-align:right">${money(item.lineTotalAmount)}</td>
        </tr>`,
    )
    .join("");
  const payments = bill.payments
    .map(
      (payment) =>
        `<p>${payment.method.replaceAll("_", " ")}: <b>${money(payment.amount)}</b></p>`,
    )
    .join("");
  const windowRef = window.open("", "_blank", "width=900,height=900");
  if (!windowRef) return;

  windowRef.document.write(`<!doctype html><html><head><title>${bill.receiptNumber}</title><style>body{font:13px Arial;color:#1f2937;padding:28px}.page{max-width:860px;margin:auto}h1{color:#7a2238;margin:0}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{padding:9px;border-bottom:1px solid #e5e7eb;text-align:left}.total{font-size:18px;color:#7a2238;border-top:2px solid #7a2238;padding-top:10px;text-align:right}@media print{body{padding:0}}</style></head><body><div class="page"><h1>CHARMS</h1><h3>${bill.store.name}</h3><p>${[bill.store.addressLine1, bill.store.city, bill.store.state].filter(Boolean).join(", ")}</p><hr/><p><b>Receipt:</b> ${bill.receiptNumber}<br/><b>Date:</b> ${new Date(bill.completedAt).toLocaleString("en-IN")}<br/><b>Customer:</b> ${bill.customer?.fullName || "Walk-in Customer"}<br/><b>Counter:</b> ${bill.counter.name}</p><table><thead><tr><th>#</th><th>Item</th><th style="text-align:right">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">GST rate</th><th style="text-align:right">GST included</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table><div class="total">GST included: ${money(bill.taxAmount)}<br/>Total: ${money(bill.totalAmount)}</div><div>${payments}</div><p style="text-align:center;color:#6b7280;margin-top:30px">System generated POS bill</p></div><script>window.onload=()=>window.print()</script></body></html>`);
  windowRef.document.close();
}

export default function PosBillsPage() {
  const { accessToken } = useAuth();
  const [filters, setFilters] = useState({
    from: indiaDate(),
    to: indiaDate(),
    search: "",
  });
  const [bills, setBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingBill, setLoadingBill] = useState(false);
  const [error, setError] = useState("");

  const loadBills = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getPosBills(filters, accessToken);
      setBills(response.sales || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load POS bills.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, filters]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const viewBill = async (receiptNumber, shouldPrint = false) => {
    setLoadingBill(true);
    setError("");
    try {
      const bill = await getPosBillReceipt(receiptNumber, accessToken);
      setSelectedBill(bill);
      if (shouldPrint) printBill(bill);
    } catch (requestError) {
      setError(requestError.message || "Unable to load the POS bill.");
    } finally {
      setLoadingBill(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-medium text-primary-800">Sales / POS</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">POS Bills</h1>
          <p className="mt-2 text-gray-500">
            Search, review, and print POS bills from every accessible store and date.
          </p>
        </div>
        <button
          type="button"
          onClick={loadBills}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 font-semibold text-white"
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[180px_180px_minmax(0,1fr)_auto]">
          <label>
            <span className="mb-2 block text-sm font-medium text-gray-700">From</span>
            <input
              type="date"
              value={filters.from}
              onChange={(event) =>
                setFilters((value) => ({ ...value, from: event.target.value }))
              }
              className="h-11 w-full rounded-xl border border-gray-200 px-3"
            />
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium text-gray-700">To</span>
            <input
              type="date"
              value={filters.to}
              onChange={(event) =>
                setFilters((value) => ({ ...value, to: event.target.value }))
              }
              className="h-11 w-full rounded-xl border border-gray-200 px-3"
            />
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium text-gray-700">Search</span>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={filters.search}
                onChange={(event) =>
                  setFilters((value) => ({ ...value, search: event.target.value }))
                }
                placeholder="Receipt, customer, cashier or phone..."
                className="h-11 w-full rounded-xl border border-gray-200 pl-10 pr-3"
              />
            </div>
          </label>
          <button
            type="button"
            onClick={loadBills}
            className="mt-7 h-11 rounded-xl border border-primary-200 px-5 font-semibold text-primary-800 hover:bg-primary-50"
          >
            Apply
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1250px] w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-4">Receipt</th>
                <th className="px-5 py-4">Date & time</th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">Store / Counter</th>
                <th className="px-5 py-4">Cashier</th>
                <th className="px-5 py-4 text-center">Items</th>
                <th className="px-5 py-4 text-right">GST by product</th>
                <th className="px-5 py-4 text-right">Total</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-5 py-16 text-center text-gray-500">
                    Loading POS bills...
                  </td>
                </tr>
              ) : bills.length ? (
                bills.map((bill) => (
                  <tr key={bill.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-4 font-mono font-bold text-primary-800">
                      {bill.receiptNumber}
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {new Date(bill.completedAt).toLocaleString("en-IN")}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-gray-900">
                        {bill.customer?.fullName || "Walk-in Customer"}
                      </p>
                      <p className="text-xs text-gray-500">{bill.customer?.phone || "—"}</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium">{bill.store.name}</p>
                      <p className="text-xs text-gray-500">{bill.counter.name}</p>
                    </td>
                    <td className="px-5 py-4">
                      {bill.createdByUser?.employee?.fullName || bill.createdByUser?.email || "—"}
                    </td>
                    <td className="px-5 py-4 text-center">{bill._count?.items || 0}</td>
                    <td className="px-5 py-4"><GstSummary rows={bill.gstSummary} /></td>
                    <td className="px-5 py-4 text-right font-bold text-primary-800">
                      {money(bill.totalAmount)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => viewBill(bill.receiptNumber)}
                          className="grid h-9 w-9 place-items-center rounded-lg border border-gray-200 text-gray-700 hover:bg-primary-50"
                          aria-label={`View ${bill.receiptNumber}`}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          onClick={() => viewBill(bill.receiptNumber, true)}
                          className="grid h-9 w-9 place-items-center rounded-lg border border-gray-200 text-gray-700 hover:bg-primary-50"
                          aria-label={`Print ${bill.receiptNumber}`}
                        >
                          <FiPrinter />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-5 py-16 text-center text-gray-500">
                    No POS bills match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedBill && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-gray-950/50 p-4">
          <div className="mx-auto my-8 max-w-5xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b p-6">
              <div>
                <h2 className="text-xl font-bold">{selectedBill.receiptNumber}</h2>
                <p className="text-sm text-gray-500">
                  {selectedBill.store.name} · {new Date(selectedBill.completedAt).toLocaleString("en-IN")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedBill(null)}
                className="grid h-10 w-10 place-items-center rounded-xl hover:bg-gray-100"
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => printBill(selectedBill)}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white"
                >
                  <FiPrinter />
                  Print bill
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="border-b text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-3">Item</th>
                      <th className="py-3 text-right">Qty</th>
                      <th className="py-3 text-right">Rate</th>
                      <th className="py-3 text-right">GST rate</th>
                      <th className="py-3 text-right">GST included</th>
                      <th className="py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBill.items.map((item, index) => (
                      <tr key={`${item.skuSnapshot}-${index}`} className="border-b">
                        <td className="py-3">
                          <p className="font-semibold">{item.productNameSnapshot}</p>
                          <p className="text-xs text-gray-500">
                            {item.variantNameSnapshot || "Default"} · {item.skuSnapshot}
                          </p>
                        </td>
                        <td className="py-3 text-right">{item.quantity}</td>
                        <td className="py-3 text-right">{money(item.unitPrice)}</td>
                        <td className="py-3 text-right font-semibold text-primary-800">
                          {gstLabel(item.taxRate)}
                        </td>
                        <td className="py-3 text-right">{money(item.lineTaxAmount)}</td>
                        <td className="py-3 text-right font-semibold">{money(item.lineTotalAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ml-auto mt-5 max-w-xs border-t pt-3 text-right">
                <p className="text-sm text-gray-500">GST included: {money(selectedBill.taxAmount)}</p>
                <p className="mt-2 text-xl font-bold text-primary-800">
                  Total: {money(selectedBill.totalAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingBill && !selectedBill && (
        <div className="fixed inset-0 z-[110] grid place-items-center bg-gray-950/30">
          <div className="rounded-2xl bg-white px-5 py-4 text-sm font-medium shadow-xl">
            Loading POS bill…
          </div>
        </div>
      )}
    </div>
  );
}
